import React, { ComponentProps, useId, useState } from 'react';

import ChevronIcon from '@/assets/chevron.svg?react';
import { RESPONSIVE_QUERY } from '@/consts';
import cn from '@/utils/cn';

import { ScrollArea } from './ScrollArea';
import SelectItem from './SelectItem';
import { i18n } from '@lingui/core';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva } from 'class-variance-authority';
import { FieldValues } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

interface TriggerProps {
  error?: string;
  disabled?: boolean;
  success?: boolean;
  valueClassName?: string;
}

type OptionObject = {
  value: string;
  label: string;
};
interface Props
  extends ComponentProps<typeof SelectPrimitive.Root>,
    TriggerProps,
    FieldValues {
  placeholder?: string;
  onChange: (value: string) => void;
  options: OptionObject[];
}

export const selectClasses = cva(
  cn(
    'relative inline-flex w-full items-center justify-between rounded-md border border-dark-gray-100',
    'h-[52px] bg-white px-3.5 py-[13px] pr-[28px] text-gray-200 outline-none hover:border-blue-600 focus:border-blue-600',
    'data-[state=open]:border-blue-600 data-[state=open]:text-oxford-blue-500',
    'focus:ring-4 focus:ring-primary/20',
    'truncate transition-colors duration-200 ease-in-out',
    'disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-dark-gray-200'
  ),
  {
    variants: {
      screen: {
        mobile: ['appearance-none'],
      },
    },
  }
);

const Select = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      placeholder = i18n.t('Wybierz'),
      error,
      disabled = false,
      success = false,
      valueClassName = '',
      className = '',
      options = [],
      onChange,
      value,
      'data-testid': dataTestId,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const isDesktop = useMediaQuery({
      query: RESPONSIVE_QUERY.IS_DESKTOP,
    });
    const id = useId();
    return (
      <>
        {!isDesktop && (
          <div className="relative">
            <div className="pointer-events-none absolute right-4 top-1/2 z-30 flex -translate-y-1/2 items-center gap-2.5">
              <ChevronIcon
                width={14}
                height={14}
                color={'#8890A3'}
                className={cn(
                  'shrink-0 rotate-90 fill-[currentColor] transition-transform duration-300 ease-in-out',
                  isOpen && 'rotate-[270deg]'
                )}
              />
            </div>
            <select
              id={id}
              name={id}
              value={value ?? ''}
              className={cn(
                selectClasses({ screen: 'mobile' }),
                'pr-[50px]',
                !!error && 'border-red-400',
                success &&
                  'border-green-500 hover:border-green-500 focus:border-green-500',
                disabled && 'cursor-not-allowed',
                value ? 'text-oxford-blue-400' : 'text-dark-gray-200',
                valueClassName
              )}
              onChange={(e) => onChange(e.target.value)}
              {...props}
            >
              <option value="" disabled hidden>
                {placeholder}
              </option>
              {options.map((option) => (
                <option
                  key={id + option.value + option.label}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {isDesktop && (
          <div
            className={cn(
              'relative hidden w-full flex-col gap-1 lg:flex',
              className
            )}
            ref={ref}
          >
            <SelectPrimitive.Root
              value={value}
              onOpenChange={() => setIsOpen(!isOpen)}
              onValueChange={(newValue) => {
                onChange(newValue);
              }}
              {...props}
            >
              <SelectPrimitive.Trigger
                id={id}
                aria-label={placeholder}
                disabled={disabled}
                className={cn(
                  selectClasses(),
                  !!error && 'border-red-400',
                  success &&
                    'border-green-500 hover:border-green-500 focus:border-green-500',
                  disabled && 'cursor-not-allowed'
                )}
                data-testid={dataTestId || `select-trigger-${props?.name}`}
              >
                <span className="flex flex-row items-center justify-start gap-4 text-left">
                  <p
                    className={cn(
                      'body text-oxford-blue-500',
                      !value && 'text-dark-gray-200',
                      valueClassName
                    )}
                  >
                    <SelectPrimitive.Value placeholder={value || placeholder} />
                  </p>
                </span>
              </SelectPrimitive.Trigger>
              <div className="pointer-events-none absolute right-0 top-1/2 flex -translate-y-1/2 cursor-pointer items-center gap-2.5 pr-4">
                <SelectPrimitive.SelectIcon className="pointer-events-none text-gray-200 [&[data-state=open]>div>svg]:rotate-[360deg]">
                  <div className="flex items-center gap-2.5">
                    <ChevronIcon
                      width={14}
                      height={14}
                      color={'#8890A3'}
                      className={cn(
                        'shrink-0 rotate-90 fill-[currentColor] transition-transform duration-300 ease-in-out',
                        isOpen && 'rotate-[270deg]'
                      )}
                    />
                  </div>
                </SelectPrimitive.SelectIcon>
              </div>
              <SelectPrimitive.Portal>
                <SelectPrimitive.Content
                  ref={(ref) =>
                    ref?.addEventListener('touchend', (e) => e.preventDefault())
                  }
                  position="popper"
                  sideOffset={-2}
                  className={cn(
                    'z-[10000] my-2 bg-white p-1.5',
                    'w-[var(--radix-select-trigger-width)]',
                    'data-[state=open]:duration-150 data-[state=closed]:ease-in-out data-[state=open]:ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[side=bottom]:slide-in-from-top-2',
                    'rounded-lg border border-gray-200 shadow-shadow14'
                  )}
                >
                  <ScrollArea mah={'max-h-[266px]'}>
                    <SelectPrimitive.Viewport className="w-full">
                      <SelectPrimitive.Group
                        className={cn(
                          'flex flex-col gap-1 border-gray-400',
                          options.length > 7 && 'pr-3.5'
                        )}
                      >
                        {options.map((option) => {
                          return (
                            <SelectItem
                              key={`${id}-${option.value}`}
                              value={option.value}
                              data-testid={`${dataTestId || 'select-item'}-${option.value}`}
                            >
                              {option.label}
                            </SelectItem>
                          );
                        })}
                      </SelectPrimitive.Group>
                    </SelectPrimitive.Viewport>
                  </ScrollArea>
                </SelectPrimitive.Content>
              </SelectPrimitive.Portal>
            </SelectPrimitive.Root>
          </div>
        )}
      </>
    );
  }
);

Select.displayName = 'Select';
export default Select;
